import { AbortError } from '@studio/js-utils/async';
// Spec 4 (D1): per-wait attribution for the room-path LiveKit sequence.
import { timeBootHop } from '#~/logger/forward.js';
import type { AppId } from '@studio/models/app';
import type { Room } from '@studio/models/room';
import { LiveKitImpl } from './livekit.js';
import type { LiveKit } from './types.js';
import { createToken } from '#~/api/livekit/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { diagnosticIdentity } from './diagnostic-identity.js';
import { makeBroker } from '#~/models/broker/index.js';
import type { Token } from '@studio/models/livekit';
import { locateProxy } from '#~/agent/utils.js';
import type { Device } from '@studio/models/device';
//import type { NewtsConnection } from '#~/newts/connection.js';
import { toIdFromHex } from '#~/api/index.js';
// PostHog feature flag for bandwidth governor — gates progressive video degradation
import { PostHogManager } from '#~/posthog/index.js';
import { PostHogFeatureFlag } from '#~/posthog/constants.js';
import { logger } from '#~/logger/index.js';

const liveKitBroker = makeBroker({
  /**
   * Create a LiveKit connection.
   *
   * @param roomId - Room ID.
   * @param args - Factory arguments.
   * @param args.appId - Application ID.
   * @param args.liveKitDeviceResolver - LiveKit device resolver.
   * @param args.signal - Abort signal (optional)
   * @param args.token - Token used for the first mint.
   * @param args.getAuthToken - Fresh Auth0 access token for later re-mints.
   * @param args.localEncoderId - Local encoder ID (optional)
   * @param args.conn - Newts connection.
   * @returns LiveKit proxy.
   */
  async create(
    roomId: Room['id'],
    args: {
      appId: AppId;
      localEncoderIds: Uint8Array[];
      signal?: AbortSignal;
      token: string;
      getAuthToken?: () => Promise<string>;
      mode: 'local' | 'cloud';
    }
  ): Promise<LiveKit> {
    const { appId, signal, localEncoderIds, token, getAuthToken, mode } = args;

    if (signal?.aborted) {
      throw new AbortError(signal.reason ?? 'livekit/broker/create aborted.');
    }

    let abortListener: (() => void) | undefined;
    const aborted = signal
      ? new Promise<never>((_resolve, reject) => {
          abortListener = () => {
            reject(new AbortError(signal.reason ?? 'livekit/broker/create aborted.'));
          };
          signal.addEventListener('abort', abortListener, { once: true });
          if (signal.aborted) abortListener();
        })
      : undefined;
    const awaitOrAbort = <T>(operation: Promise<T>): Promise<T> =>
      aborted ? Promise.race([operation, aborted]) : operation;

    try {
      // Resolve the bandwidth governor feature flag before constructing the
      // LiveKit connection. When enabled, LiveKitImpl turns on adaptiveStream,
      // dynacast, and the progressive degradation governor.
      // Per rule: architecture-principles (ai.knowledge/global) — flag-gated module
      let bandwidthGovernorEnabled = false;
      try {
        const manager = PostHogManager.getInstance();
        // Spec 4 (D1): review-4 §4 identifies this as a REAL awaited wait on the
        // room path — flag readiness defaults to 5s — so it is measured
        // separately from the token group and from liveKit.start() below.
        // awaitOrAbort/cleanup/flag defaults are unchanged.
        bandwidthGovernorEnabled = await awaitOrAbort(
          timeBootHop(
            'livekit.bandwidth-governor-flag',
            manager.getFeatureFlag(PostHogFeatureFlag.BANDWIDTH_GOVERNOR, {
              defaultValue: false
            }),
            'livekit'
          )
        );
      } catch (error) {
        if (signal?.aborted) throw error;
        // PostHog not initialized or flag fetch failed — default to disabled.
        bandwidthGovernorEnabled = false;
      }

      // ENG-4174 / ENG-4247: mint the live STUDIO token and — on web (cloud) only —
      // a SEPARATE diagnostic token with a DISTINCT identity (`<appId>-lkdiag`), in
      // parallel so the diagnostic mint adds no boot latency. The ENG-3868 startup
      // probe runs a real LiveKit ConnectionCheck (a genuine SFU join); if it reused
      // the live STUDIO token it would join with the live participant's identity and
      // the SFU would evict the live session via DUPLICATE_IDENTITY — permanently
      // killing the cloud transport (the #571 regression). The diagnostic mint is
      // failure-swallowed so it can NEVER block the live connection (probe spec C7);
      // on desktop the probe never fires, so we skip the mint entirely.
      // Per rule: reference-rules-in-comments (ai.knowledge/global).
      const [studio, diagnosticToken] = await awaitOrAbort(
        timeBootHop(
          'livekit.create-token',
          Promise.all([
            createToken(appId, roomId, token, 'STUDIO'),
            isElectronApp()
              ? Promise.resolve<Token | undefined>(undefined)
              : createToken(diagnosticIdentity(appId), roomId, token, 'STUDIO-DIAG')
                  .then((diag) => <Token>diag.token)
                  .catch(() => undefined)
          ]),
          'livekit'
        )
      );
      const { token: lkToken, endpoint } = studio;

      const deviceLookup = (deviceId: string) => {
        const proxy = locateProxy(<Device['id']>deviceId);

        return toIdFromHex(proxy?.id);
      };

      // Re-mint closes over a fresh Auth0 token so an expired LiveKit credential
      // can be replaced without reloading the page. The one-shot `token` above
      // remains the first mint.
      const mintCloudToken = async (): Promise<{ token: Token; endpoint: string }> =>
        createToken(appId, roomId, await (getAuthToken?.() ?? Promise.resolve(token)), 'STUDIO');

      // Construct the LiveKit connection proxy, passing the governor flag and
      // the optional diagnostic-probe token (constructor param order).
      const liveKit = new LiveKitImpl(
        roomId,
        <Token>lkToken,
        endpoint,
        appId,
        localEncoderIds,
        mode,
        deviceLookup,
        bandwidthGovernorEnabled,
        diagnosticToken,
        mintCloudToken
      );

      try {
        await awaitOrAbort(timeBootHop('livekit.start', liveKit.start(), 'livekit'));
        // Close the same-turn race where startup resolves immediately before cancellation.
        if (signal?.aborted) {
          throw new AbortError(signal.reason ?? 'livekit/broker/create aborted.');
        }
        return liveKit;
      } catch (error) {
        // A failed factory result is never registered, so it must clean up itself.
        let cleanupError: unknown;
        try {
          await liveKit.stop();
        } catch (stopError) {
          cleanupError = stopError;
        } finally {
          liveKit.clearListeners();
        }

        if (cleanupError) {
          logger.error('LiveKit startup cleanup failed.', cleanupError);
        }
        throw error;
      }
    } finally {
      if (abortListener) {
        signal?.removeEventListener('abort', abortListener);
      }
    }
  },

  /**
   * Destroy the LiveKit connection.
   *
   * @param liveKit - LiveKit connection proxy to be destroyed.
   */
  async destroy(liveKit: LiveKit): Promise<void> {
    const proxy = <LiveKitImpl>liveKit;

    // Stop the connection proxy.
    try {
      await proxy.stop();
    } finally {
      // Listener cleanup must run even when the SDK rejects disconnect.
      proxy.clearListeners();
    }
  }
});

/** LiveKit resource broker. */
export const broker = Object.assign(liveKitBroker, {
  onSystemResume(payload?: { sleepMs?: number | null }): void {
    for (const liveKit of liveKitBroker) {
      (liveKit as LiveKitImpl).onSystemResume(payload);
    }
  }
});
